import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Shield, Globe, Copy, Check, Share2, ArrowLeft, AlertTriangle, Info,
  ExternalLink, ChevronDown, ChevronUp, Activity, HelpCircle, Download,
  RefreshCw, AlertCircle, Lock, Bot, FileText, ShieldAlert, ShieldCheck, Radar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { api, getScoreColor, getScoreLabel, getCategoryLabel, getCategoryColor, formatNumber } from '@/lib/api';
import { StatusBanner, TechnicalDetails, ConfidenceBadge } from '@/pages/ReportComponents';

function AgentCapabilitiesCard({ data, domain, publicId }) {
  const [shareUrl, setShareUrl] = useState(null);
  const [sharing, setSharing] = useState(false);

  const handleShareCard = async () => {
    setSharing(true);
    try {
      const imgUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/sharecard/ai-agent-report/image/by-public/${publicId}.png`;
      window.open(imgUrl, '_blank');
      setShareUrl(imgUrl);
    } catch (err) {
      console.error('Share card error:', err);
    } finally {
      setSharing(false);
    }
  };

  if (!data) return null;
  const { files_found = [], robots_ai_bots = [], notes = [] } = data;

  const foundFiles = files_found.filter(f => f.status === 200 && f.path !== '/robots.txt');
  const blockedBots = robots_ai_bots.filter(b => b.allowed === false);
  const allowedBots = robots_ai_bots.filter(b => b.allowed === true);
  const knownBots = robots_ai_bots.filter(b => !b.extra);
  const extraBots = robots_ai_bots.filter(b => b.extra);
  const hasSignals = robots_ai_bots.length > 0 || foundFiles.length > 0;

  return (
    <div className="mt-8 bg-white rounded-2xl border border-slate-200 shadow-soft overflow-hidden" data-testid="agent-capabilities-card">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center">
          <Radar className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Agent Capabilities</h3>
          <p className="text-xs text-slate-500">AI agent signals detected on {domain}</p>
        </div>
        {hasSignals ? (
          <span className="ml-auto px-2.5 py-1 text-xs font-medium rounded-full bg-purple-50 text-purple-700">{robots_ai_bots.length} bot rule{robots_ai_bots.length !== 1 ? 's' : ''}</span>
        ) : (
          <span className="ml-auto px-2.5 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-500">No signals</span>
        )}
        {hasSignals && publicId && (
          <button
            onClick={handleShareCard}
            disabled={sharing}
            className="ml-2 px-3 py-1.5 text-xs font-medium rounded-full bg-purple-600 text-white hover:bg-purple-700 transition-colors flex items-center gap-1.5"
            data-testid="share-ai-agent-card-btn"
          >
            <Share2 className="w-3 h-3" />Share AI Report
          </button>
        )}
      </div>

      <div className="p-6 space-y-5">
        {/* Robots.txt AI Bot Rules */}
        {knownBots.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
              <Bot className="w-4 h-4 text-slate-400" />
              AI Bot Access (robots.txt)
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {knownBots.map((bot) => (
                <div key={bot.bot} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs border ${
                  bot.allowed === false
                    ? 'bg-red-50/50 border-red-100 text-red-700'
                    : bot.allowed === 'partial'
                    ? 'bg-amber-50/50 border-amber-100 text-amber-700'
                    : 'bg-emerald-50/50 border-emerald-100 text-emerald-700'
                }`} title={`Confidence: ${bot.confidence || 'high'}${bot.via ? ' (via wildcard)' : ''}`}>
                  {bot.allowed === false ? <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0" /> :
                   bot.allowed === 'partial' ? <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> :
                   <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" />}
                  <span className="font-medium truncate">{bot.bot}</span>
                  {bot.via === 'wildcard' && <span className="text-[9px] opacity-50">*</span>}
                </div>
              ))}
            </div>
            {extraBots.length > 0 && (
              <details className="mt-2">
                <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                  + {extraBots.length} more bot rule{extraBots.length !== 1 ? 's' : ''} detected
                </summary>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-2">
                  {extraBots.map((bot) => (
                    <div key={bot.bot} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs border ${
                      bot.allowed === false
                        ? 'bg-red-50/50 border-red-100 text-red-700'
                        : 'bg-emerald-50/50 border-emerald-100 text-emerald-700'
                    }`}>
                      {bot.allowed === false ? <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0" /> : <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" />}
                      <span className="font-medium truncate">{bot.bot}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
            <div className="flex gap-4 mt-3">
              {blockedBots.length > 0 && (
                <span className="text-xs text-red-600 font-medium">{blockedBots.length} blocked</span>
              )}
              {allowedBots.length > 0 && (
                <span className="text-xs text-emerald-600 font-medium">{allowedBots.length} allowed</span>
              )}
            </div>
          </div>
        )}

        {/* Agent Capability Files */}
        {foundFiles.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-400" />
              Agent Capability Files
            </h4>
            <div className="space-y-2">
              {foundFiles.map((f) => (
                <div key={f.path} className="flex items-center gap-3 px-4 py-2.5 bg-purple-50/50 border border-purple-100 rounded-lg">
                  <FileText className="w-4 h-4 text-purple-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-mono text-purple-700">{f.path}</span>
                    {f.excerpt && <p className="text-xs text-slate-500 mt-0.5 truncate">{f.excerpt}</p>}
                  </div>
                  <a
                    href={`https://${domain}${f.path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-purple-600 hover:underline flex-shrink-0"
                  >
                    View
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary Notes */}
        {notes.length > 0 && (
          <div className="bg-slate-50 rounded-lg p-3">
            {notes.map((note, i) => (
              <p key={i} className="text-xs text-slate-600 flex items-start gap-2">
                <Info className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
                {note}
              </p>
            ))}
          </div>
        )}

        {!hasSignals && (
          <p className="text-sm text-slate-500 text-center py-2">No AI agent rules or capability files detected on this site.</p>
        )}
      </div>
    </div>
  );
}

export default function ReportPage() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showAllDomains, setShowAllDomains] = useState(false);
  const [showAboutScan, setShowAboutScan] = useState(false);

  useEffect(() => {
    api.getReport(reportId)
      .then(setReport)
      .catch(() => setError('Report not found'))
      .finally(() => setLoading(false));
  }, [reportId]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Failed to copy link'); }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: `AgentCheck Report for ${report?.domain}`, url: window.location.href }); }
      catch (err) { if (err.name !== 'AbortError') handleCopyLink(); }
    } else { handleCopyLink(); }
  };

  const handleDownloadShareCard = async () => {
    try {
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${api.getShareCardImageUrl(reportId)}`;
      const resp = await fetch(fullUrl);
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `agentcheck-${report?.domain || reportId}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Share card downloaded!');
    } catch { toast.error('Failed to download share card'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full border-4 border-slate-200 border-t-[#007AFF] animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading report...</p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Report Not Found</h2>
          <p className="text-slate-600 mb-6">The requested report could not be found.</p>
          <Button onClick={() => navigate('/')} variant="outline"><ArrowLeft className="w-4 h-4 mr-2" />Back to Home</Button>
        </div>
      </div>
    );
  }

  const scanStatus = report.scan_status || 'complete';
  const rh = report.render_hints || { badge: 'complete', show_partial_banner: false, show_install_monitor_cta: true, show_technical_details: false, can_share: true };
  const aps = report.aps;
  const totals = report.totals || {};
  const isPartial = scanStatus.startsWith('partial');
  const isBlocked = scanStatus === 'blocked';
  const isError = scanStatus === 'error';
  const apsScore = aps?.score ?? report.aps_score ?? 0;
  const scoreColor = getScoreColor(apsScore);
  const scoreLabel = getScoreLabel(apsScore);
  const reqCount = totals.requests_observed ?? totals.total_requests ?? 0;

  const categoryData = Array.isArray(report.categories)
    ? report.categories.map(c => ({ ...c, label: getCategoryLabel(c.category), color: getCategoryColor(c.category) }))
    : Object.entries(report.categories || {}).map(([category, count]) => ({ category, count, percent: 0, label: getCategoryLabel(category), color: getCategoryColor(category) })).sort((a, b) => b.count - a.count);
  const totalCatReqs = categoryData.reduce((s, c) => s + c.count, 0);

  const domains3p = report.third_party_domains || [];
  const displayDomains = showAllDomains ? domains3p : domains3p.slice(0, 10);
  const companies = report.companies || [];
  const hasResults = domains3p.length > 0 || companies.length > 0 || (report.script_sources || []).length > 0 || (report.preconnect_domains || []).length > 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-slate-600 hover:text-slate-900" data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
            <div className="w-8 h-8 rounded-lg bg-[#007AFF] flex items-center justify-center"><Shield className="w-5 h-5 text-white" strokeWidth={2} /></div>
            <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>AgentCheck</span>
          </button>
          {rh.can_share && (
            <Button onClick={handleShare} variant="outline" className="gap-2" data-testid="share-button">
              {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}{copied ? 'Copied!' : 'Share Report'}
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <StatusBanner report={report} />

        {/* Score Header for complete + partial */}
        {!isBlocked && !isError && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-8 mb-6" data-testid="report-header">
            <div className="flex items-center gap-3 mb-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Scan Report</p>
              {isPartial && <ConfidenceBadge confidence={report.confidence} isPartial />}
            </div>
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="relative w-40 h-40 flex-shrink-0">
                <svg className="w-full h-full -rotate-90">
                  <circle cx="80" cy="80" r="70" fill="none" stroke="#E2E8F0" strokeWidth="12" />
                  <circle cx="80" cy="80" r="70" fill="none" stroke={scoreColor} strokeWidth="12" strokeLinecap="round" strokeDasharray={`${apsScore * 4.4} 440`} className="transition-all duration-1000" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-4xl font-bold" style={{ color: scoreColor, fontFamily: 'JetBrains Mono, monospace' }} data-testid="aps-score">{apsScore}</span>
                  <span className="text-sm text-slate-500 font-medium">{aps?.is_partial ? 'APS (partial)' : 'APS'}</span>
                </div>
              </div>
              <div className="flex-1 text-center md:text-left">
                <div className="flex items-center gap-2 justify-center md:justify-start mb-2">
                  <Globe className="w-5 h-5 text-slate-400" />
                  <span className="text-lg font-mono text-slate-700" data-testid="report-domain">{report.domain}</span>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                  Agent Pressure Score: <span style={{ color: scoreColor }}>{scoreLabel}</span>
                </h1>
                <p className="text-slate-600">
                  {isPartial
                    ? `Partial scan captured ${totals.third_party_domains || 0} third-party domains across ${totals.companies || 0} companies.`
                    : `This site contacted ${totals.third_party_domains || 0} third-party domains across ${totals.companies || 0} companies.`}
                </p>
                <div className="flex items-center gap-6 mt-6 justify-center md:justify-start">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono' }}>{totals.third_party_domains || 0}</div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider">Domains</div>
                  </div>
                  <div className="h-8 w-px bg-slate-200" />
                  <div className="text-center">
                    <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono' }}>{totals.companies || 0}</div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider">Companies</div>
                  </div>
                  <div className="h-8 w-px bg-slate-200" />
                  <TooltipProvider><Tooltip><TooltipTrigger asChild>
                    <div className="text-center cursor-help">
                      <div className="text-2xl font-bold text-slate-900 flex items-center gap-1" style={{ fontFamily: 'JetBrains Mono' }}>{formatNumber(reqCount)}<HelpCircle className="w-3 h-3 text-slate-400" /></div>
                      <div className="text-xs text-slate-500 uppercase tracking-wider">Requests</div>
                    </div>
                  </TooltipTrigger><TooltipContent><p className="text-xs max-w-xs">Observed during a short scan window.</p></TooltipContent></Tooltip></TooltipProvider>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Blocked state */}
        {isBlocked && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-8 mb-6 text-center" data-testid="blocked-state">
            <Lock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <div className="flex items-center gap-2 justify-center mb-2"><Globe className="w-5 h-5 text-slate-400" /><span className="text-lg font-mono text-slate-700" data-testid="report-domain">{report.domain}</span></div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Scan blocked by site protection</h2>
            <p className="text-slate-600 max-w-lg mx-auto mb-6">This site blocked automated access before we could capture reliable tracker signals. Use AgentCheck Monitor to browse the site normally and get a complete report.</p>
            <div className="flex items-center justify-center gap-3">
              <Button className="bg-[#007AFF] hover:bg-[#0062CC] rounded-full gap-2" onClick={() => navigate('/monitor')} data-testid="blocked-install-monitor"><Activity className="w-4 h-4" />Install Monitor (Browser)</Button>
              <Button variant="outline" className="rounded-full" onClick={() => navigate('/')} data-testid="blocked-try-another">Try another URL</Button>
            </div>
          </div>
        )}

        {/* Error state */}
        {isError && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-8 mb-6 text-center" data-testid="error-state">
            <AlertCircle className="w-16 h-16 text-red-300 mx-auto mb-4" />
            <div className="flex items-center gap-2 justify-center mb-2"><Globe className="w-5 h-5 text-slate-400" /><span className="text-lg font-mono text-slate-700" data-testid="report-domain">{report.domain}</span></div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Scan error</h2>
            <p className="text-slate-600 max-w-lg mx-auto mb-6">Something went wrong on our side. Please try again.</p>
            <div className="flex items-center justify-center gap-3">
              <Button className="bg-[#007AFF] hover:bg-[#0062CC] rounded-full gap-2" onClick={() => navigate('/')} data-testid="error-retry-btn"><RefreshCw className="w-4 h-4" />Retry scan</Button>
              <Button variant="outline" className="rounded-full" onClick={() => navigate('/')}>Try another URL</Button>
            </div>
          </div>
        )}

        {/* "Scan using Monitor" workflow for partial/blocked */}
        {(isPartial || isBlocked) && (
          <div className="bg-white rounded-2xl border-2 border-[#007AFF]/20 shadow-soft p-8 mb-8" data-testid="scan-using-monitor-workflow">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 mb-3">
                <Activity className="w-4 h-4 text-[#007AFF]" />
                <span className="text-sm font-medium text-[#007AFF]">{isBlocked ? 'Alternative available' : 'Get complete results'}</span>
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                {isBlocked ? 'This site blocks automated scans — Monitor gets the full picture' : 'Want complete results? Use Monitor while browsing.'}
              </h2>
              <p className="text-sm text-slate-500 max-w-xl mx-auto">
                AgentCheck Monitor runs in your browser with your consent. No bypass tricks — just transparent, real-time tracking visibility.
              </p>
            </div>
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-slate-50 rounded-xl p-4 text-center">
                <div className="w-10 h-10 rounded-full bg-[#007AFF]/10 flex items-center justify-center mx-auto mb-3">
                  <span className="text-[#007AFF] font-bold text-sm">1</span>
                </div>
                <h4 className="text-sm font-semibold text-slate-900 mb-1">Install Monitor</h4>
                <p className="text-xs text-slate-500">Add the Chrome extension. Monitoring is OFF by default.</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 text-center">
                <div className="w-10 h-10 rounded-full bg-[#007AFF]/10 flex items-center justify-center mx-auto mb-3">
                  <span className="text-[#007AFF] font-bold text-sm">2</span>
                </div>
                <h4 className="text-sm font-semibold text-slate-900 mb-1">Browse {report.domain}</h4>
                <p className="text-xs text-slate-500">Enable monitoring and visit the site normally in your browser.</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 text-center">
                <div className="w-10 h-10 rounded-full bg-[#007AFF]/10 flex items-center justify-center mx-auto mb-3">
                  <span className="text-[#007AFF] font-bold text-sm">3</span>
                </div>
                <h4 className="text-sm font-semibold text-slate-900 mb-1">See Full Results</h4>
                <p className="text-xs text-slate-500">Monitor captures every third-party request — no blockers, complete data.</p>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3">
              <Button className="bg-[#007AFF] hover:bg-[#0062CC] rounded-full gap-2 h-11 px-6" onClick={() => navigate('/monitor')} data-testid="workflow-install-monitor-btn">
                <Activity className="w-4 h-4" />Install Monitor (Free)
              </Button>
            </div>
            <p className="text-xs text-slate-400 text-center mt-3">All data stored locally on your device. No URLs or page content collected.</p>
          </div>
        )}

        {/* Monitor CTA for complete scans */}
        {rh.show_install_monitor_cta && !isBlocked && !isError && !isPartial && (
          <div className="bg-gradient-to-r from-blue-50 to-white rounded-2xl border border-blue-100 p-6 mb-8" data-testid="monitor-cta-strip">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#007AFF]/10 flex items-center justify-center flex-shrink-0"><Activity className="w-6 h-6 text-[#007AFF]" /></div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Want this for your device?</h3>
                  <p className="text-sm text-slate-600 mt-1">Install Monitor to see which companies contact you over time.</p>
                  <p className="text-xs text-slate-500 mt-2">Consent-first. No page content. Domain-level counts only.</p>
                </div>
              </div>
              <Button className="bg-[#007AFF] hover:bg-[#0062CC] rounded-full flex-shrink-0" onClick={() => navigate('/monitor')} data-testid="install-monitor-btn">Install Monitor</Button>
            </div>
          </div>
        )}

        {/* Results grid */}
        {hasResults && (
          <div id="results-section" className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              {/* Categories */}
              {categoryData.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6" data-testid="categories-section">
                  <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Tracker Categories</h2>
                  <p className="text-sm text-slate-500 mb-6">What kinds of automation ran behind the scenes.</p>
                  <div className="space-y-4">
                    {categoryData.map(cat => (
                      <div key={cat.category}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`category-badge category-${cat.category}`} data-testid={`category-badge-${cat.category}`}>{cat.category === 'UNKNOWN' ? 'Unclassified' : cat.label}</span>
                          <span className="text-sm font-mono text-slate-600">{cat.count} ({totalCatReqs > 0 ? Math.round((cat.count / totalCatReqs) * 100) : 0}%)</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all duration-700" style={{ width: `${totalCatReqs > 0 ? (cat.count / totalCatReqs) * 100 : 0}%`, backgroundColor: cat.color }} /></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Companies */}
              {companies.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6" data-testid="companies-section">
                  <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Top Companies Detected</h2>
                  <div className="divide-y divide-slate-100">
                    {companies.slice(0, 10).map((co, i) => {
                      const unmapped = co.mapped === false || co.name === 'Unmapped';
                      return (
                        <div key={co.company_id || i} className="py-4 flex items-center justify-between" data-testid={`company-row-${i}`}>
                          <div className="flex items-center gap-4">
                            <span className="w-6 text-sm font-mono text-slate-400">#{i + 1}</span>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className={`font-semibold ${unmapped ? 'text-slate-500' : 'text-slate-900'}`}>{unmapped ? 'Unmapped' : co.name}</span>
                                {!unmapped && <span className="text-xs text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">mapped</span>}
                              </div>
                              <div className="flex gap-1.5 mt-1">{co.categories.map(c => <span key={c} className={`category-badge text-[10px] category-${c}`}>{c === 'UNKNOWN' ? 'Unclassified' : getCategoryLabel(c)}</span>)}</div>
                            </div>
                          </div>
                          <div className="text-right"><div className="font-mono font-semibold text-slate-900">{formatNumber(co.count)}</div><div className="text-xs text-slate-500">requests</div></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Domains */}
              {domains3p.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6" data-testid="domains-section">
                  <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Third-Party Domains</h2>
                  <div className="space-y-2">
                    {displayDomains.map((d, i) => (
                      <div key={d.domain} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-slate-50" data-testid={`domain-row-${i}`}>
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-sm text-slate-700">{d.domain}</span>
                          <span className={`category-badge text-[10px] category-${d.category}`}>{d.category === 'UNKNOWN' ? 'Unclassified' : getCategoryLabel(d.category)}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`text-sm ${d.mapped === false ? 'text-slate-400 italic' : 'text-slate-500'}`}>{d.mapped === false ? 'Unmapped' : (d.company_name || d.company)}</span>
                          <span className="font-mono text-sm text-slate-700">{d.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  {domains3p.length > 10 && (
                    <button onClick={() => setShowAllDomains(!showAllDomains)} className="mt-4 flex items-center gap-2 text-[#007AFF] text-sm font-medium hover:underline" data-testid="show-all-domains-button">
                      {showAllDomains ? <><ChevronUp className="w-4 h-4" />Show less</> : <><ChevronDown className="w-4 h-4" />Show all {domains3p.length} domains</>}
                    </button>
                  )}
                </div>
              )}

              {/* Static signals for partial_static */}
              {scanStatus === 'partial_static' && ((report.script_sources || []).length > 0 || (report.preconnect_domains || []).length > 0) && (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6" data-testid="static-signals-section">
                  <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Static Signals Detected</h2>
                  <p className="text-sm text-slate-500 mb-4">Script sources and connection hints found in the page HTML.</p>
                  {(report.script_sources || []).length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Script Sources</h4>
                      <div className="flex flex-wrap gap-2">{report.script_sources.map(d => <span key={d} className="px-2 py-1 bg-slate-100 rounded-md text-xs font-mono text-slate-600">{d}</span>)}</div>
                    </div>
                  )}
                  {(report.preconnect_domains || []).length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Preconnect / DNS-Prefetch</h4>
                      <div className="flex flex-wrap gap-2">{report.preconnect_domains.map(d => <span key={d} className="px-2 py-1 bg-slate-100 rounded-md text-xs font-mono text-slate-600">{d}</span>)}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Technical details */}
              {rh.show_technical_details && report.error && <TechnicalDetails error={report.error} />}

              {/* About this scan */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-soft overflow-hidden">
                <button onClick={() => setShowAboutScan(!showAboutScan)} className="w-full p-6 flex items-center justify-between text-left hover:bg-slate-50">
                  <div className="flex items-center gap-3"><Info className="w-5 h-5 text-slate-400" /><span className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>{isBlocked ? 'Why this happens' : 'About this scan'}</span></div>
                  {showAboutScan ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                </button>
                {showAboutScan && (
                  <div className="px-6 pb-6 border-t border-slate-100 pt-4">
                    <ul className="space-y-2 text-sm text-slate-600">
                      <li>Scans run in a headless browser for a short window.</li>
                      <li>Results vary by region, page state, and A/B tests.</li>
                      <li>We store domain-level results only.</li>
                      {isPartial && <li>Some sites block automated scanning. AgentCheck shows what it can capture safely.</li>}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {rh.can_share && (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6" data-testid="share-card-section">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>Share This Report</h3>
                  <div className="space-y-3">
                    <Button onClick={handleCopyLink} variant="outline" className="w-full gap-2" data-testid="copy-link-button">
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}{copied ? 'Copied!' : 'Copy Link'}
                    </Button>
                    <Button onClick={handleDownloadShareCard} variant="outline" className="w-full gap-2" data-testid="download-card-button">
                      <Download className="w-4 h-4" />Download Share Card
                    </Button>
                  </div>
                </div>
              )}
              {apsScore > 0 && (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6" data-testid="score-explanation">
                  <div className="flex items-center gap-2 mb-4"><Info className="w-5 h-5 text-slate-400" /><h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>About APS</h3></div>
                  <p className="text-sm text-slate-600 leading-relaxed mb-4">APS estimates third-party automation pressure. It's explainable, not a black box.</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-emerald-500" /><span className="text-slate-600">0-25: Low</span></div>
                    <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-amber-500" /><span className="text-slate-600">26-50: Moderate</span></div>
                    <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-orange-500" /><span className="text-slate-600">51-75: High</span></div>
                    <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-red-500" /><span className="text-slate-600">76-100: Very high</span></div>
                  </div>
                </div>
              )}
              {(report.script_sources || []).length > 0 && scanStatus !== 'partial_static' && (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6" data-testid="evidence-section">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>Script Sources</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {report.script_sources.slice(0, 10).map((s, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs font-mono text-slate-500 truncate"><ExternalLink className="w-3 h-3 flex-shrink-0" /><span className="truncate">{s}</span></div>
                    ))}
                  </div>
                </div>
              )}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Run Another Scan</h3>
                <p className="text-sm text-slate-600 mb-4">Check the privacy score of any website for free.</p>
                <Button onClick={() => navigate('/')} className="w-full bg-[#007AFF] hover:bg-[#0062CC]" data-testid="new-scan-button">New Scan</Button>
              </div>
            </div>
          </div>
        )}

        {/* Technical details outside grid for blocked/error with no results */}
        {!hasResults && rh.show_technical_details && report.error && (
          <div className="max-w-2xl mx-auto mt-4"><TechnicalDetails error={report.error} /></div>
        )}

        {/* Agent Capabilities Card */}
        {report.agent_capabilities && (
          <AgentCapabilitiesCard data={report.agent_capabilities} domain={report.domain} publicId={reportId} />
        )}

        {/* Footer CTA */}
        {!isError && (
          <div className="mt-12 bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-8 text-center" data-testid="footer-cta">
            <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Manrope' }}>{isPartial || isBlocked ? 'Protected sites need Monitor.' : 'This is a snapshot. Monitor is the movie.'}</h3>
            <p className="text-slate-300 max-w-2xl mx-auto mb-6">{isPartial || isBlocked ? 'Some sites block automated scans. Monitor captures data while you browse — no bypass, full consent.' : 'Scan shows one site, one window. Monitor shows everything, all day.'}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8" onClick={() => navigate('/monitor')} data-testid="footer-install-monitor"><Activity className="w-4 h-4 mr-2" />Install Monitor</Button>
              <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 rounded-full px-8" onClick={() => navigate('/')}>Run another scan</Button>
            </div>
          </div>
        )}
      </main>
      <footer className="py-8 border-t border-slate-100 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-slate-500">Report generated on {new Date(report.created_at).toLocaleDateString()} | Made with AgentCheck</p>
        </div>
      </footer>
    </div>
  );
}
